import { useEffect, useRef, useState } from "react";

type VideoItem = {
  id: string;
  title: string;
  subtitle: string;
  src: string;
  poster?: string;
};

const videos: VideoItem[] = [
  {
    id: "helmet",
    title: "디자인 인 모션",
    subtitle: "헬멧과 자연스럽게 어우러지는 X9 PRO의 디자인",
    src: `${__BASE_PATH__}videos/x9pro-helmet.mp4`,
    poster: `${__BASE_PATH__}images/video-helmet-poster.png`,
  },
  {
    id: "ride",
    title: "로드 무드",
    subtitle: "라이딩의 속도와 감각을 담아낸 브랜드 무드",
    src: `${__BASE_PATH__}videos/x9pro-ride.mp4`,
    poster: `${__BASE_PATH__}images/video-ride-poster.png`,
  },
  {
    id: "brand-03",
    title: "X9 PRO 브랜드 필름 03",
    subtitle: "라이딩의 움직임과 X9 PRO의 감각을 담은 영상",
    src: `${__BASE_PATH__}videos/x9pro-brand-03.mp4`,
  },
  {
    id: "brand-04",
    title: "X9 PRO 브랜드 필름 04",
    subtitle: "도로 위에서 완성되는 X9 PRO의 브랜드 무드",
    src: `${__BASE_PATH__}videos/x9pro-brand-04.mp4`,
  },
  {
    id: "brand-05",
    title: "X9 PRO 브랜드 필름 05",
    subtitle: "라이더의 시선으로 표현한 X9 PRO",
    src: `${__BASE_PATH__}videos/x9pro-brand-05.mp4`,
  },
  {
    id: "brand-06",
    title: "X9 PRO 브랜드 필름 06",
    subtitle: "속도와 감각을 담아낸 새로운 라이딩 장면",
    src: `${__BASE_PATH__}videos/x9pro-brand-06.mp4`,
  },
];

export default function VideoSection() {
  const [playing, setPlaying] = useState<Record<string, boolean>>({});
  const [videoPage, setVideoPage] = useState(0);
  const refs = useRef<Record<string, HTMLVideoElement | null>>({});
  const rotationVideos = [...videos.slice(2), ...videos.slice(0, 2)];
  const videosPerPage = 2;
  const pageCount = Math.ceil(rotationVideos.length / videosPerPage);
  const visibleVideos = rotationVideos.slice(
    videoPage * videosPerPage,
    videoPage * videosPerPage + videosPerPage,
  );

  useEffect(() => {
    if (Object.values(playing).some(Boolean)) return;

    const timer = window.setInterval(() => {
      setVideoPage((current) => (current + 1) % pageCount);
    }, 7000);

    return () => window.clearInterval(timer);
  }, [pageCount, playing]);

  const togglePlay = (id: string) => {
    const video = refs.current[id];
    if (!video) return;
    if (video.paused) {
      video.play();
      setPlaying((p) => ({ ...p, [id]: true }));
    } else {
      video.pause();
      setPlaying((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <section id="videos" className="w-full bg-background-900 py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <span className="inline-block font-heading tracking-[0.32em] text-xs font-semibold text-primary-500">
            BRAND FILM
          </span>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight tracking-tight">
            라이딩의 순간을 담다
          </h2>
          <p className="mt-4 text-background-300 leading-relaxed">
            X9 PRO가 지향하는 디자인과 라이딩 무드를 시각적으로 표현한
            브랜드 필름입니다. 일부 장면은 AI로 연출되어 실제 제품과 차이가 있을 수 있습니다.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {visibleVideos.map((v) => (
            <div
              key={v.id}
              className="group relative rounded-2xl overflow-hidden bg-background-950 border border-background-800/60 aspect-[16/10]"
            >
              <video
                ref={(el) => { refs.current[v.id] = el; }}
                src={v.src}
                poster={v.poster}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="metadata"
                onLoadedMetadata={(event) => {
                  if (!v.poster && event.currentTarget.duration > 0.1) {
                    event.currentTarget.currentTime = 0.1;
                  }
                }}
                onClick={() => togglePlay(v.id)}
              ></video>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                {!playing[v.id] && (
                  <div className="flex flex-col items-center gap-4">
                    <button
                      type="button"
                      onClick={() => togglePlay(v.id)}
                      className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 hover:bg-primary-400 text-background-50 flex items-center justify-center transition-transform hover:scale-105 cursor-pointer pointer-events-auto shadow-lg"
                    >
                      <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
                    </button>
                    <div className="text-center">
                      <div className="font-heading text-lg md:text-xl font-bold text-background-50">{v.title}</div>
                      <div className="text-xs text-background-400 mt-1">{v.subtitle}</div>
                    </div>
                  </div>
                )}
              </div>

              {playing[v.id] && (
                <button
                  type="button"
                  onClick={() => togglePlay(v.id)}
                  className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-background-950/80 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-900 transition-colors pointer-events-auto backdrop-blur-sm"
                >
                  <i className="ri-pause-fill text-lg"></i>
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2" aria-label="영상 목록 선택">
          {Array.from({ length: pageCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setVideoPage(index)}
              aria-label={`${index + 1}번째 영상 묶음 보기`}
              aria-current={videoPage === index ? "true" : undefined}
              className={`h-2.5 rounded-full transition-all ${
                videoPage === index
                  ? "w-8 bg-primary-500"
                  : "w-2.5 bg-background-600 hover:bg-background-500"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
